import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, forwardRef, inject } from '@angular/core';
import { BasketViewComponentDef } from '@df/basket/view/basket-view.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { OrderByPipe } from '@df/ui/common/order-by.pipe';
import { PriceService, TaxMode } from '@df/ui/price/price.service';
import { merge } from 'rxjs';
import { MinibasketItemComponent } from '../basket/minibasket-item.component';
import { BasketViewTotalsComponent } from '../basket/totals/basket-view-totals.component';
import { CheckoutFormComponent } from './checkout-form.component';
import { CouponFormComponent } from '../basket/coupon-form.component';
import { RewardBannerComponent } from '../basket/reward-banner/reward-banner.component';
import { IfNotTradeStoreDirective } from '../multisite/trade/if-not-trade-store.directive';
import { GiftcardsFormComponent } from '../module/giftcards/giftcards-form.component';
import { LocalBasketItem } from '../customer/basket/basket-item.decorator';
import { LocalBasket } from '../basket/local-basket';
export interface ITotal {
    label: string;
    value: number;
    comment?: string;
    id: string; // string id
}
@Component({
    selector: 'checkout-minibasket',
    templateUrl: './checkout-minibasket.html',
    standalone: true,
    imports: [
        IfNotTradeStoreDirective,
        GiftcardsFormComponent,
        OrderByPipe,
        CouponFormComponent,
        NgClass,
        MinibasketItemComponent,
        RewardBannerComponent,
        forwardRef(() => BasketViewTotalsComponent)
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckoutMinibasketComponent extends BasketViewComponentDef<LocalBasket> {
    private priceService = inject(PriceService);
    /**
     * Totals to print (except GT)
     */
    totals: ITotal[] = [];
    /**
     * Class constructor
     */
    protected checkoutFormComponent = inject(CheckoutFormComponent);

    override ngOnInit() {
        this.subscriptions.add(
            merge(
                this.app.events.on(Checkout.EVENTS.TotalsUpdate)
                // this.basket.basketUpdateSubject,
                // this.app.prefs.obs.currency
            ).subscribe(() => this.update())
        );
        this.update();
    }
    ngOnChanges() {
        this.update();
    }
    private get withShipping() {
        return this.checkoutFormComponent.hasShippingMethod;
    }
    getGiftCardDiscount(taxMode: TaxMode = TaxMode.skip): number {
        return this.basket.getTotal('giftcards', 'discount_tax', taxMode);
    }
    /**
     * Updates totals
     */
    protected update() {
        const totals = this.basket.data.totals;
        const output: ITotal[] = [];
        if (totals) {
            const isTrade = false;
            const showTax = this.priceService.showCatalogPricesInclTax;
            const taxSuffix = isTrade ? (showTax ? ' (inc. VAT)' : ' (exl. VAT)') : '';
            const totalsExlTax = isTrade && !showTax;
            // collect subtotal with/without tax
            const subtotal = totalsExlTax ? this.basket.getSubtotal(TaxMode.excluding) : this.basket.getSubtotal(TaxMode.including);
            // collect discount with/without tax
            const discount = totalsExlTax ? this.basket.getDiscount(TaxMode.excluding) : this.basket.getDiscount(TaxMode.including);
            // collect gift card discount with/without tax
            const giftCardDiscount = totalsExlTax
                ? this.getGiftCardDiscount(TaxMode.excluding)
                : this.getGiftCardDiscount(TaxMode.including);
            // subtotal
            if (discount || this.withShipping || isTrade) {
                output.push({
                    label: 'Subtotal' + taxSuffix,
                    id: 'subtotal',
                    value: subtotal
                });
            }
            // discount
            if (discount) {
                output.push({
                    label: 'Discount' + taxSuffix,
                    id: 'discount',
                    value: discount
                });
            }
            // delivery
            if (this.withShipping) {
                const shippingPriceIncTax = this.basket.getShipping(TaxMode.including);
                const finalShippingPriceIncTax = shippingPriceIncTax;
                const deliveryLabel = this.app.multisite.siteId === 'us' ? 'Shipping' : 'Delivery';
                output.push({
                    label: deliveryLabel + taxSuffix,
                    comment: this.checkoutFormComponent.shippingMethod?.title,
                    id: 'shipping',
                    value: totalsExlTax
                        ? // this magic here is to reduce proprtionally since we only have points discount inc vat
                          this.basket.getShipping(TaxMode.excluding) * (finalShippingPriceIncTax / shippingPriceIncTax)
                        : finalShippingPriceIncTax
                });
            }
            // gift card discount
            if (giftCardDiscount) {
                output.push({
                    label: 'Gift Cards' + taxSuffix,
                    id: 'giftcards',
                    value: -giftCardDiscount
                });
            }
            // vat
            output.push({
                label: 'Tax',
                id: 'tax',
                value: (totals.tax || 0) - (this.withShipping ? 0 : totals.shipping_tax || 0)
            });
            output.push({
                label: isTrade ? 'Total (inc. Tax)' : 'Total',
                id: 'total',
                value: totals.grand_total
            });
        }
        this.totals = output;
    }

    protected get items() {
        return (this.basket?.items as LocalBasketItem[]) || [];
    }
}
